import ThemedSelect from "../shared/ThemedSelect.jsx";
import { Form } from "react-bootstrap";
import { DEFAULT_PRINT_OPTIONS } from "./QuoteView.jsx";
import { PRINT_MODES, applyMode, detectMode, modeByKey } from "./quotePrintModes.js";

// The print SIDE PANEL (2026-09-08; Robert, from medispace's Proposal tab:
// "the options are on the side panel… part of me likes a modes idea"). The
// document is chosen at the top — Quotation, Estimate, Scope of Works, the
// layouts KS knows by name — and the sections below are the adjustments
// that still apply to that document; nothing on the panel is ever
// irrelevant to what is being printed. 1.0's option panel (QuoteReport.aspx
// div#printoptions) is all still here, regrouped; the mapping is documented
// atop QuoteView.jsx. Never prints (no-print); every change re-renders the
// letter instantly.

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-uppercase small text-muted fw-semibold mb-1">{title}</div>
      {children}
    </div>
  );
}

// Quantities in the price column's place — three-way, because "the wording
// didn't always say quantity" (Robyn 2026-09-10) and some documents want the
// number alone. Offered on a quote (instead of prices) and on a Scope of Works
// (which has no prices to replace). Works under either heading layout: the
// column is per line, the headings are per section.
function QuantityChoice({ o, set }) {
  const value = o.showQty === "qty" ? "qty" : o.showQty ? "unit" : "none";
  return (
    <ThemedSelect
      size="sm" value={value}
      onChange={(e) => set({ showQty: e.target.value === "none" ? false : e.target.value })}
      data-hint="A Quantity column where the price would be: the number with its unit (5 m2), or the number alone"
    >
      <option value="none">No quantities</option>
      <option value="unit">Qty &amp; unit</option>
      <option value="qty">Qty only</option>
    </ThemedSelect>
  );
}

function QuotePrintOptions({ options, onChange, doc }) {
  // Merged against the defaults, exactly as QuoteView does: the panel and the
  // document MUST read the same object (Robert 2026-08-11).
  const o = { ...DEFAULT_PRINT_OPTIONS, ...options };
  const set = (patch) => onChange({ ...o, ...patch });
  const hasLocations = (doc?.summaries?.room || []).some((r) => r.label !== "(No location)");
  const hasHeaders = (doc?.summaries?.header || []).length > 0;
  const hasOverheads = Number(doc?.overheads_total) > 0;
  const mode = detectMode(o);
  const sow = !!o.sow;
  const priced = !o.hidePrices && !sow;

  return (
    <div className="no-print quote-print-panel">
      <Section title="Document">
        {PRINT_MODES.filter((m) => m.key !== "headerOnly" || hasHeaders).map((m) => (
          <Form.Check
            key={m.key} type="radio" name="qp-mode" id={`qp-mode-${m.key}`}
            label={m.label} data-hint={m.hint}
            checked={mode === m.key}
            onChange={() => onChange(applyMode(o, m.key))}
          />
        ))}
        {mode === "custom" ? (
          <div className="small text-muted mt-1">
            Custom — adjusted past a preset.{" "}
            {o.mode && modeByKey(o.mode) ? (
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onChange(applyMode(o, o.mode))}>
                Back to {modeByKey(o.mode).label}
              </button>
            ) : null}
          </div>
        ) : null}
      </Section>

      <Section title="Headings">
        {/* HEADINGS, not order: lines always print in entered order (1.0's own
            default); this picks which headings are drawn over them. */}
        <ThemedSelect
          size="sm" className="mb-1" value={o.headings}
          onChange={(e) => set({ headings: e.target.value })}
          data-hint="Which headings are drawn over the lines. Lines always print in the order they were entered."
        >
          <option value="location">Floor &amp; room headings</option>
          <option value="quote">Quote header sections</option>
        </ThemedSelect>
        {hasHeaders && !sow ? (
          <ThemedSelect
            size="sm" value={o.detail}
            // Header subtotals only implies the quote's headers, so it sets the
            // headings too instead of greying them out (Robert 2026-09-07).
            onChange={(e) => set(e.target.value === "summaryOnly" ? { detail: "summaryOnly", headings: "quote" } : { detail: e.target.value })}
            data-hint="Full lines, or just each quote header with its subtotal (1.0's Header Only)"
          >
            <option value="full">Full detail</option>
            <option value="summaryOnly">Header subtotals only</option>
          </ThemedSelect>
        ) : null}
      </Section>

      {sow ? (
        <Section title="Quantities">
          <QuantityChoice o={o} set={set} />
        </Section>
      ) : (
        <Section title="Money">
          <Form.Check
            type="switch" id="qp-hide-prices" label="Hide all prices"
            checked={o.hidePrices} onChange={(e) => set({ hidePrices: e.target.checked })}
            data-hint="No line prices, no totals, price-free wording — the letter stays a quote (for the works alone, print the Scope of Works)"
          />
          <div className="small fw-semibold mt-2 mb-1">Quantities instead of prices</div>
          <QuantityChoice o={o} set={set} />
          {/* Off = the client quote; on = the internal one with our margins.
              Disabled on a price-free print, where the money columns have
              nothing to show. */}
          <Form.Check
            type="switch" id="qp-all-details" label="All details (internal — shows margins)"
            checked={!!o.allDetails} disabled={o.hidePrices}
            onChange={(e) => set({ allDetails: e.target.checked })}
            data-hint="Off = client quote (description + price) · on = internal (unit, quantity, margin)"
          />
          {hasOverheads && !o.separateMargin ? (
            <Form.Check
              type="switch" id="qp-overheads" label="Separate overheads"
              checked={o.separateOverheads} onChange={(e) => set({ separateOverheads: e.target.checked })}
              data-hint="Overheads lines leave the detail and print as one Preliminaries & Generals row above the totals"
            />
          ) : null}
          {/* The Vero layout, as switches — the modes above tick them together. */}
          <Form.Check
            type="switch" id="qp-room-totals" label="Room section totals"
            checked={!!o.roomTotals} onChange={(e) => set({ roomTotals: e.target.checked })}
            data-hint="Numbered sections (1.00, 2.00 …), each closing with a Total for that room"
          />
          <Form.Check
            type="switch" id="qp-separate-margin" label="Costs + margin line (Vero)"
            checked={!!o.separateMargin} disabled={o.hidePrices}
            onChange={(e) => set({ separateMargin: e.target.checked })}
            data-hint="Line amounts at cost; one Builders Overheads and Profit line in the summary ($ and margin %). The totals reconcile with the standard print to the cent."
          />
          <div className="small fw-semibold mt-2 mb-1">Columns</div>
          {/* Off while quantities replace the price: the column is already the quantity. */}
          {/* Tri-state (KS 2026-08-20): "qty and unit" must NOT print the Rate column. */}
          <Form.Check
            type="radio" name="qp-rate-mode" id="qp-rate-off" label="Price only"
            checked={!o.rateColumns} disabled={!priced || o.showQty}
            onChange={() => set({ rateColumns: false })}
            data-hint="The standard shape — description and price"
          />
          <Form.Check
            type="radio" name="qp-rate-mode" id="qp-rate-unit" label="Qty & unit"
            checked={o.rateColumns === "unit"} disabled={!priced || o.showQty}
            onChange={() => set({ rateColumns: "unit" })}
            data-hint="Qty, Unit and Amount columns — no rate"
          />
          <Form.Check
            type="radio" name="qp-rate-mode" id="qp-rate-rate" label="Qty & rate"
            checked={!!o.rateColumns && o.rateColumns !== "unit"} disabled={!priced || o.showQty}
            onChange={() => set({ rateColumns: "rate" })}
            data-hint="Qty, Unit, Rate and Amount columns (Vero/AA line shape). Rate is the unit cost rate — on a marked-up line rate × qty is less than the sell amount."
          />
          <div className="small fw-semibold mt-2 mb-1">Summary section</div>
          <ThemedSelect
            size="sm" value={o.summary} disabled={o.hidePrices}
            onChange={(e) => set({ summary: e.target.value })}
            data-hint="An extra subtotal summary printed after the totals"
          >
            <option value="none">None</option>
            <option value="work_centre">By Work Centre</option>
            {hasLocations ? <option value="room">By Room</option> : null}
            {hasLocations ? <option value="work_centre_room">By WC within Room</option> : null}
            {hasHeaders ? <option value="header">By Quote Header</option> : null}
          </ThemedSelect>
        </Section>
      )}

      <Section title="Wording & layout">
        {sow ? null : (
          <Form.Check
            type="switch" id="qp-estimate" label="Present as estimate"
            checked={o.estimate} onChange={(e) => set({ estimate: e.target.checked })}
            data-hint="Swaps quote → estimate throughout the wording"
          />
        )}
        {priced ? (
          <Form.Check
            type="switch" id="qp-hide-zero" label="Hide zero amounts"
            checked={o.hideZero} onChange={(e) => set({ hideZero: e.target.checked })}
          />
        ) : null}
        <Form.Check
          type="switch" id="qp-bordered" label="Bordered table"
          checked={o.bordered} onChange={(e) => set({ bordered: e.target.checked })}
        />
      </Section>
    </div>
  );
}

export default QuotePrintOptions;
